import {
  AnimationAction,
  AnimationMixer,
  Group,
  LoopOnce,
  LoopRepeat,
  MathUtils,
  Mesh,
  Object3D,
  Scene,
  Vector3,
  type PerspectiveCamera,
} from 'three';
import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { CharacterLoader } from '../character/CharacterModel';
import { WORLD } from '../config';
import {
  NPC_SEND_HZ,
  SPIDER_ANIMS,
  SPIDER_CLIPS,
  SPIDER_DAMAGE,
  SPIDER_MAX_HP,
  SPIDER_RESPAWN_SECONDS,
  type NpcTuple,
  type SpiderAnim,
} from '../net/protocol';
import type { HeightField } from '../world/HeightField';

/** Kolik pavouků se ve světě drží. */
const SPIDER_COUNT = 6;
/**
 * V jakém okruhu od počátku se rodí.
 *
 * Devadesát metrů znamenalo, že je hráč ve vysoké trávě prostě nenašel.
 */
const SPAWN_RADIUS = 34;
/** Odkud pavouk začne pronásledovat. */
const AGGRO_RANGE = 18;
/** Na jakou vzdálenost kousne. */
const ATTACK_RANGE = 2.4;
/** Jak často může kousnout. */
const ATTACK_COOLDOWN = 1.6;
const WALK_SPEED = 3.2;
/** Model má skoro šest metrů; tohle z něj udělá půldruhametrového pavouka. */
const SPIDER_SCALE = 0.34;

export interface SpiderTarget {
  id: string;
  position: Vector3;
  alive: boolean;
}

export interface SpiderEvents {
  /** Pavouk kousl hráče. */
  onBite?: (targetId: string, damage: number) => void;
  /** Stav nepřátel se má rozeslat ostatním. */
  onBroadcast?: (state: NpcTuple[]) => void;
}

interface Spider {
  id: number;
  root: Group;
  mixer: AnimationMixer | null;
  actions: Map<string, AnimationAction>;
  current: SpiderAnim;
  hp: number;
  home: Vector3;
  /** Cíl, na kterého útočí; null = klidný stav. */
  targetId: string | null;
  attackCooldown: number;
  respawnIn: number;
  /** Kam se interpoluje, když pavouka simuluje někdo jiný. */
  netTarget: Vector3 | null;
  netYaw: number;
  bar: HTMLElement;
}

/**
 * Pavouci ve světě.
 *
 * **Model vlastnictví:** nepřátele simuluje vždy právě jeden hráč — ten
 * s nejmenším id v místnosti. Ostatní jeho stav jen přijímají a plynule
 * dopočítávají mezi snímky. Když majitel odejde, převezme to další v pořadí;
 * protože si všichni drží poslední přijatý stav, pavouci nezmizí ani
 * neskočí.
 *
 * Je to kompromis, ne autorita: majitel může lhát. Pro test proti pár
 * pavoukům je to v pořádku, ale jakmile bude na výsledku záležet,
 * musí simulace přejít na server — architektura s jedním Durable Objectem
 * na zónu to unese.
 *
 * Pozice hnízd se počítají z herního seedu, takže je všichni klienti
 * odvodí stejně a nemusí se posílat.
 */
export class SpiderManager {
  private readonly spiders: Spider[] = [];
  private readonly group = new Group();
  private readonly barLayer: HTMLElement;
  private readonly projected = new Vector3();
  private readonly toTarget = new Vector3();

  private template: Object3D | null = null;
  private clips: Map<string, import('three').AnimationClip> = new Map();
  private owner = false;
  private sendTimer = 0;

  constructor(
    scene: Scene,
    private readonly loader: CharacterLoader,
    private readonly heightField: HeightField,
    parent: HTMLElement,
    private readonly events: SpiderEvents = {},
  ) {
    this.group.name = 'spiders';
    scene.add(this.group);

    this.barLayer = document.createElement('div');
    this.barLayer.id = 'enemy-bars';
    parent.appendChild(this.barLayer);
  }

  get count(): number {
    return this.spiders.length;
  }

  get isOwner(): boolean {
    return this.owner;
  }

  /** Nastaví, jestli tenhle klient nepřátele simuluje. */
  setOwner(owner: boolean): void {
    this.owner = owner;
  }

  async preload(): Promise<void> {
    if (this.template) return;
    const gltf = await this.loader.loadGLTF('/models/combat/spider.glb');
    for (const clip of gltf.animations) this.clips.set(clip.name, clip);
    this.template = gltf.scene;
    this.spawnAll();
  }

  /** Zásah do pavouka. Uplatní se jen u toho, kdo nepřátele simuluje. */
  damage(id: number, amount: number): void {
    if (!this.owner) return;
    const spider = this.spiders.find((s) => s.id === id);
    if (!spider || spider.hp <= 0) return;

    spider.hp = Math.max(0, spider.hp - amount);
    if (spider.hp === 0) {
      this.play(spider, 'death', LoopOnce);
      spider.respawnIn = SPIDER_RESPAWN_SECONDS;
      spider.targetId = null;
    }
  }

  /**
   * Najde nejbližšího živého pavouka v dosahu seknutí.
   *
   * Vrací jeho id, ne odkaz — zásah putuje po síti a tam se odkazy na
   * objekty přenést nedají.
   */
  findTargetInArc(origin: Vector3, yaw: number, range: number, halfAngle: number): number | null {
    let best: number | null = null;
    let bestDistance = range;

    for (const spider of this.spiders) {
      if (spider.hp <= 0) continue;
      this.toTarget.subVectors(spider.root.position, origin);
      const distance = this.toTarget.length();
      if (distance > bestDistance) continue;

      // Úhel mezi směrem pohledu a pavoukem — sekat se dá jen dopředu.
      const angle = Math.abs(
        MathUtils.euclideanModulo(Math.atan2(this.toTarget.x, this.toTarget.z) - yaw + Math.PI, Math.PI * 2) -
          Math.PI,
      );
      if (angle > halfAngle) continue;

      best = spider.id;
      bestDistance = distance;
    }
    return best;
  }

  /** Stav přijatý od hráče, který nepřátele simuluje. */
  applyRemoteState(state: NpcTuple[]): void {
    if (this.owner) return;
    for (const [id, x, y, z, yaw, anim, hp] of state) {
      const spider = this.spiders.find((s) => s.id === id);
      if (!spider) continue;
      spider.netTarget = spider.netTarget ?? new Vector3();
      spider.netTarget.set(x, y, z);
      spider.netYaw = yaw;
      spider.hp = hp;
      const nextAnim = SPIDER_ANIMS[anim] ?? 'idle';
      if (nextAnim !== spider.current) {
        this.play(spider, nextAnim, nextAnim === 'death' || nextAnim === 'attack' ? LoopOnce : LoopRepeat);
      }
    }
  }

  update(dt: number, targets: SpiderTarget[], camera: PerspectiveCamera, canvas: HTMLElement): void {
    for (const spider of this.spiders) {
      if (this.owner) this.simulate(spider, dt, targets);
      else this.followRemote(spider, dt);
      spider.mixer?.update(dt);
      this.positionBar(spider, camera, canvas);
    }

    if (!this.owner) return;
    this.sendTimer += dt;
    if (this.sendTimer >= 1 / NPC_SEND_HZ) {
      this.sendTimer = 0;
      this.events.onBroadcast?.(this.spiders.map((s) => this.toTuple(s)));
    }
  }

  dispose(): void {
    for (const spider of this.spiders) spider.bar.remove();
    this.spiders.length = 0;
    this.group.removeFromParent();
    this.barLayer.remove();
  }

  // --- vnitřní ----------------------------------------------------------

  private spawnAll(): void {
    for (let i = 0; i < SPIDER_COUNT; i++) {
      const home = this.findHome(i);
      const spider = this.createSpider(i, home);
      if (spider) this.spiders.push(spider);
    }
  }

  /**
   * Hnízdo pavouka. Pozice vychází z herního seedu a pořadí, takže ji
   * všichni klienti spočítají stejně a nemusí se posílat po síti.
   */
  private findHome(index: number): Vector3 {
    const golden = 2.399963;
    for (let attempt = 0; attempt < 24; attempt++) {
      const angle = (index * 7 + attempt) * golden;
      const radius = SPAWN_RADIUS * (0.25 + ((index * 13 + attempt * 5) % 100) / 130);
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      const ground = this.heightField.heightAt(x, z);
      // Pavouci nepatří do vody ani na sráz.
      if (ground > WORLD.seaLevel + 1.5) {
        const normal = this.heightField.normalAt(x, z);
        if (normal[1] > 0.85) return new Vector3(x, ground, z);
      }
    }
    return new Vector3(index * 4, this.heightField.heightAt(index * 4, 0), 0);
  }

  private createSpider(id: number, home: Vector3): Spider | null {
    if (!this.template) return null;

    const root = new Group();
    root.name = `spider_${id}`;
    root.position.copy(home);
    root.scale.setScalar(SPIDER_SCALE);

    // SkeletonUtils.clone, ne Object3D.clone.
    //
    // Pavouk je skinovaný mesh. Obyčejný klon sdílí geometrii, ale jeho
    // kostra pořád ukazuje na kosti originálu, které v klonu nejsou —
    // skinning pak model složí do bodu. Objekt existuje, hlásí se jako
    // viditelný, a přesto se nevykreslí ani pixel.
    const model = SkeletonUtils.clone(this.template);
    model.traverse((child) => {
      if (child instanceof Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        child.frustumCulled = false;
      }
    });
    root.add(model);
    this.group.add(root);

    const mixer = new AnimationMixer(model);
    const actions = new Map<string, AnimationAction>();
    for (const [name, clip] of this.clips) actions.set(name, mixer.clipAction(clip));

    const bar = document.createElement('div');
    bar.className = 'enemy-bar';
    bar.innerHTML = '<i></i>';
    bar.style.display = 'none';
    this.barLayer.appendChild(bar);

    const spider: Spider = {
      id,
      root,
      mixer,
      actions,
      current: 'idle',
      hp: SPIDER_MAX_HP,
      home: home.clone(),
      targetId: null,
      attackCooldown: 0,
      respawnIn: 0,
      netTarget: null,
      netYaw: 0,
      bar,
    };
    this.play(spider, 'idle', LoopRepeat);
    return spider;
  }

  private play(spider: Spider, anim: SpiderAnim, loop: typeof LoopOnce | typeof LoopRepeat): void {
    const action = spider.actions.get(SPIDER_CLIPS[anim]);
    if (!action) return;

    const previous = spider.actions.get(SPIDER_CLIPS[spider.current]);
    spider.current = anim;

    action.reset();
    action.setLoop(loop, loop === LoopOnce ? 1 : Infinity);
    // Smrt musí zůstat v poslední pozici, jinak by mrtvý pavouk vyskočil
    // zpátky do klidové pózy.
    action.clampWhenFinished = loop === LoopOnce;
    action.enabled = true;

    if (previous && previous !== action) action.crossFadeFrom(previous, 0.18, false);
    action.play();
  }

  private simulate(spider: Spider, dt: number, targets: SpiderTarget[]): void {
    if (spider.hp <= 0) {
      spider.respawnIn -= dt;
      if (spider.respawnIn <= 0) {
        spider.hp = SPIDER_MAX_HP;
        spider.root.position.copy(spider.home);
        this.play(spider, 'idle', LoopRepeat);
      }
      return;
    }

    spider.attackCooldown -= dt;

    // Nejbližší živý hráč v dosahu.
    let target: SpiderTarget | null = null;
    let bestDistance = AGGRO_RANGE;
    for (const candidate of targets) {
      if (!candidate.alive) continue;
      const distance = spider.root.position.distanceTo(candidate.position);
      if (distance < bestDistance) {
        bestDistance = distance;
        target = candidate;
      }
    }

    spider.targetId = target?.id ?? null;

    if (!target) {
      if (spider.current !== 'idle') this.play(spider, 'idle', LoopRepeat);
      return;
    }

    this.toTarget.subVectors(target.position, spider.root.position);
    this.toTarget.y = 0;
    const distance = this.toTarget.length();
    spider.root.rotation.y = Math.atan2(this.toTarget.x, this.toTarget.z);

    if (distance > ATTACK_RANGE) {
      if (spider.current !== 'walk') this.play(spider, 'walk', LoopRepeat);
      this.toTarget.normalize().multiplyScalar(WALK_SPEED * dt);
      spider.root.position.add(this.toTarget);
      spider.root.position.y = this.heightField.heightAt(spider.root.position.x, spider.root.position.z);
      return;
    }

    if (spider.attackCooldown <= 0) {
      spider.attackCooldown = ATTACK_COOLDOWN;
      this.play(spider, 'attack', LoopOnce);
      this.events.onBite?.(target.id, SPIDER_DAMAGE);
    }
  }

  /** Plynulé dotažení k poslední přijaté pozici. */
  private followRemote(spider: Spider, dt: number): void {
    if (!spider.netTarget) return;
    spider.root.position.lerp(spider.netTarget, Math.min(1, dt * 10));
    spider.root.rotation.y += shortestAngle(spider.root.rotation.y, spider.netYaw) * Math.min(1, dt * 10);
  }

  private toTuple(spider: Spider): NpcTuple {
    return [
      spider.id,
      round(spider.root.position.x),
      round(spider.root.position.y),
      round(spider.root.position.z),
      round(spider.root.rotation.y, 3),
      SPIDER_ANIMS.indexOf(spider.current),
      spider.hp,
    ];
  }

  private positionBar(spider: Spider, camera: PerspectiveCamera, canvas: HTMLElement): void {
    // Ukazatel se objeví, až když pavouk něco schytal — jinak by nad
    // každým poklidným pavoukem visel plný pruh a rušil.
    if (spider.hp >= SPIDER_MAX_HP || spider.hp <= 0) {
      spider.bar.style.display = 'none';
      return;
    }

    this.projected.set(spider.root.position.x, spider.root.position.y + 1.1, spider.root.position.z);
    this.projected.project(camera);

    if (this.projected.z > 1 || Math.abs(this.projected.x) > 1.1 || Math.abs(this.projected.y) > 1.1) {
      spider.bar.style.display = 'none';
      return;
    }

    spider.bar.style.display = '';
    spider.bar.style.transform = `translate(-50%, -100%) translate(${
      (this.projected.x * 0.5 + 0.5) * canvas.clientWidth
    }px, ${(-this.projected.y * 0.5 + 0.5) * canvas.clientHeight}px)`;
    const fill = spider.bar.firstElementChild as HTMLElement | null;
    if (fill) fill.style.width = `${(spider.hp / SPIDER_MAX_HP) * 100}%`;
  }
}

function round(value: number, decimals = 2): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function shortestAngle(from: number, to: number): number {
  let diff = (to - from) % (Math.PI * 2);
  if (diff > Math.PI) diff -= Math.PI * 2;
  if (diff < -Math.PI) diff += Math.PI * 2;
  return diff;
}
